import type {
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  RefObject,
} from 'react';
import { useState } from 'react';
import { AUTOCAD_STANDARD_COLORS } from './cadColors';
import type { HatchPattern } from './types';

type PrimitiveStatusBarProps = {
  title: string;
  hint: string;
  showMultiGableActions: boolean;
  multiGableActiveStep: 'mainRakes' | 'secondaryRakes' | 'hips';
  multiGableCanSelectSecondaryRakes: boolean;
  multiGableCanSelectHips: boolean;
  multiGableCanFinalize: boolean;
  onMultiGableMainRakes: () => void;
  onMultiGableSecondaryRakes: () => void;
  onMultiGableHips: () => void;
  onMultiGableFinalize: () => void;
  backgroundReferenceInput: { value: string } | null;
  onBackgroundReferenceInputChange: (value: string) => void;
  onCommitBackgroundReference: () => void;
  referenceLengthInputRef: RefObject<HTMLInputElement | null>;
  textToolVisible?: boolean;
  textToolValue?: string;
  onTextToolValueChange?: (value: string) => void;
  textToolHeight?: string;
  onTextToolHeightChange?: (value: string) => void;
  textToolJustification?: string;
  onTextToolJustificationChange?: (value: string) => void;
  textToolColor?: string;
  onTextToolColorChange?: (value: string) => void;
  textToolFont?: string;
  onTextToolFontChange?: (value: string) => void;
  textToolFonts?: string[];
  hatchToolVisible?: boolean;
  hatchToolPattern?: HatchPattern;
  onHatchToolPatternChange?: (value: HatchPattern) => void;
  hatchToolScale?: string;
  onHatchToolScaleChange?: (value: string) => void;
  hatchToolColor?: string;
  onHatchToolColorChange?: (value: string) => void;
  hatchToolMode?: "pick" | "build";
  onHatchToolModeChange?: (value: "pick" | "build") => void;
  iconToolVisible?: boolean;
  iconToolColor?: string;
  onIconToolColorChange?: (value: string) => void;
  iconToolScale?: string;
  onIconToolScaleChange?: (value: string) => void;
  numericEditVisible: boolean;
  showDirectionInput: boolean;
  directionInputRef: RefObject<HTMLInputElement | null>;
  directionValue: string;
  onDirectionFocus: FocusEventHandler<HTMLInputElement>;
  onDirectionChange: (value: string) => void;
  onDirectionKeyDown: KeyboardEventHandler<HTMLInputElement>;
  showLineDistanceInput: boolean;
  lineDistanceInputRef: RefObject<HTMLInputElement | null>;
  lineDistanceValue: string;
  onLineDistanceFocus: FocusEventHandler<HTMLInputElement>;
  onLineDistanceChange: (value: string) => void;
  onLineDistanceKeyDown: KeyboardEventHandler<HTMLInputElement>;
  numericInputRef: RefObject<HTMLInputElement | null>;
  numericLabel: string;
  numericValue: string;
  onNumericFocus: FocusEventHandler<HTMLInputElement>;
  onNumericChange: (value: string) => void;
  onNumericKeyDown: KeyboardEventHandler<HTMLInputElement>;
  unitLabel: string;
  onCommitNumericEdit: MouseEventHandler<HTMLButtonElement>;
  activeSystemTitle?: string | null;
  activeSystemCodes?: string | null;
  activeSystemProducts?: string | null;
  historyStatusLabel: string;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: MouseEventHandler<HTMLButtonElement>;
  onRedo: MouseEventHandler<HTMLButtonElement>;
};

export function PrimitiveStatusBar({
  title,
  hint,
  showMultiGableActions,
  multiGableActiveStep,
  multiGableCanSelectSecondaryRakes,
  multiGableCanSelectHips,
  multiGableCanFinalize,
  onMultiGableMainRakes,
  onMultiGableSecondaryRakes,
  onMultiGableHips,
  onMultiGableFinalize,
  backgroundReferenceInput,
  onBackgroundReferenceInputChange,
  onCommitBackgroundReference,
  referenceLengthInputRef,
  textToolVisible = false,
  textToolValue = "",
  onTextToolValueChange,
  textToolHeight = "20",
  onTextToolHeightChange,
  textToolJustification = "left",
  onTextToolJustificationChange,
  textToolColor = "#111827",
  onTextToolColorChange,
  textToolFont = "Arial",
  onTextToolFontChange,
  textToolFonts = [],
  hatchToolVisible = false,
  hatchToolPattern = "diagonal",
  onHatchToolPatternChange,
  hatchToolScale = "1",
  onHatchToolScaleChange,
  hatchToolColor = "#111827",
  onHatchToolColorChange,
  hatchToolMode = "pick",
  onHatchToolModeChange,
  iconToolVisible = false,
  iconToolColor = "#1d5cab",
  onIconToolColorChange,
  iconToolScale = "1",
  onIconToolScaleChange,
  numericEditVisible,
  showDirectionInput,
  directionInputRef,
  directionValue,
  onDirectionFocus,
  onDirectionChange,
  onDirectionKeyDown,
  showLineDistanceInput,
  lineDistanceInputRef,
  lineDistanceValue,
  onLineDistanceFocus,
  onLineDistanceChange,
  onLineDistanceKeyDown,
  numericInputRef,
  numericLabel,
  numericValue,
  onNumericFocus,
  onNumericChange,
  onNumericKeyDown,
  unitLabel,
  onCommitNumericEdit,
  activeSystemTitle,
  activeSystemCodes,
  activeSystemProducts,
  historyStatusLabel,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
}: PrimitiveStatusBarProps) {
  const [isTextColorPaletteOpen, setIsTextColorPaletteOpen] = useState(false);
  const lightActionButtonClass =
    'inline-flex h-8 items-center justify-center rounded-md border border-[rgba(74,58,31,0.16)] bg-white px-2.5 text-[10px] font-medium text-[#1f2937] transition active:scale-110';

  return (
    <div
      className="pointer-events-none absolute bottom-2 left-1/2 z-30 -translate-x-1/2"
      aria-label="Barra di stato"
    >
      <div
        className="pointer-events-auto flex min-h-[48px] w-[min(1180px,calc(100vw-56px))] min-w-[940px] items-center gap-3 rounded-[6px] border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.96)] px-3 py-2 shadow-[0_20px_40px_rgba(58,45,20,0.14)] backdrop-blur-[10px]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="min-w-[160px]">
          <div className="text-[9px] font-bold uppercase tracking-[0.08em] text-[#4b3e27]">
            {title}
          </div>
          <div className="mt-1 text-[11px] text-[#6c5b3d]">{hint}</div>
        </div>

        <div className="flex min-w-[250px] flex-1 items-center justify-center">
          {showMultiGableActions ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={`inline-flex h-8 items-center justify-center rounded-md bg-white px-2.5 text-[10px] font-semibold text-[#1f2937] transition active:scale-110 ${
                  multiGableActiveStep === 'mainRakes'
                    ? 'border-2 border-[#1d5cab]'
                    : 'border border-[rgba(74,58,31,0.16)]'
                }`}
                onClick={onMultiGableMainRakes}
              >
                MR
              </button>
              <button
                type="button"
                disabled={!multiGableCanSelectSecondaryRakes}
                className={`inline-flex h-8 items-center justify-center rounded-md bg-white px-2.5 text-[10px] font-semibold text-[#1f2937] transition active:scale-110 disabled:cursor-not-allowed disabled:opacity-40 ${
                  multiGableActiveStep === 'secondaryRakes'
                    ? 'border-2 border-[#1d5cab]'
                    : 'border border-[rgba(74,58,31,0.16)]'
                }`}
                onClick={onMultiGableSecondaryRakes}
              >
                SR
              </button>
              <button
                type="button"
                disabled={!multiGableCanSelectHips}
                className={`inline-flex h-8 items-center justify-center rounded-md bg-white px-2.5 text-[10px] font-semibold text-[#1f2937] transition active:scale-110 disabled:cursor-not-allowed disabled:opacity-40 ${
                  multiGableActiveStep === 'hips'
                    ? 'border-2 border-[#1d5cab]'
                    : 'border border-[rgba(74,58,31,0.16)]'
                }`}
                onClick={onMultiGableHips}
              >
                H
              </button>
              <button
                type="button"
                disabled={!multiGableCanFinalize}
                className="inline-flex h-8 items-center justify-center rounded-md border border-[rgba(74,58,31,0.16)] bg-white px-2.5 text-[10px] font-semibold text-[#1f2937] transition active:scale-110 disabled:cursor-not-allowed disabled:opacity-40"
                onClick={onMultiGableFinalize}
              >
                End
              </button>
            </div>
          ) : backgroundReferenceInput ? (
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-[#4b3e27]">Rif.</span>
              <input
                ref={referenceLengthInputRef}
                className="h-8 w-24 rounded-md border border-[rgba(74,58,31,0.16)] bg-white px-2.5 text-[11px] font-medium text-[#111827] outline-none focus:border-[#1d5cab]"
                value={backgroundReferenceInput.value}
                onChange={(event) => onBackgroundReferenceInputChange(event.target.value)}
              />
              <span className="text-[11px] font-medium text-[#6b7280]">{unitLabel}</span>
              <button
                type="button"
                className={lightActionButtonClass}
                onClick={onCommitBackgroundReference}
              >
                Apply
              </button>
            </div>
          ) : hatchToolVisible ? (
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-[#4b3e27]">Hatch</span>
              <select
                className="h-8 w-28 rounded-md border border-[rgba(74,58,31,0.16)] bg-white px-2 text-[11px] font-medium text-[#111827] outline-none focus:border-[#1d5cab]"
                value={hatchToolMode}
                onChange={(event) => onHatchToolModeChange?.(event.target.value as "pick" | "build")}
              >
                <option value="pick">Pick</option>
                <option value="build">Build</option>
              </select>
              <select
                className="h-8 w-32 rounded-md border border-[rgba(74,58,31,0.16)] bg-white px-2 text-[11px] font-medium text-[#111827] outline-none focus:border-[#1d5cab]"
                value={hatchToolPattern}
                onChange={(event) => onHatchToolPatternChange?.(event.target.value as HatchPattern)}
              >
                <option value="diagonal">Diagonal</option>
                <option value="cross">Cross</option>
                <option value="horizontal">Horizontal</option>
                <option value="vertical">Vertical</option>
                <option value="grid">Grid</option>
              </select>
              <span className="text-[11px] font-medium text-[#4b3e27]">Scale</span>
              <input
                className="h-8 w-16 rounded-md border border-[rgba(74,58,31,0.16)] bg-white px-2.5 text-[11px] font-medium text-[#111827] outline-none focus:border-[#1d5cab]"
                value={hatchToolScale}
                inputMode="decimal"
                onChange={(event) => onHatchToolScaleChange?.(event.target.value)}
              />
              <input
                className="h-8 w-10 cursor-pointer rounded-md border border-[rgba(74,58,31,0.16)] bg-white p-1"
                type="color"
                value={hatchToolColor}
                aria-label="Hatch color"
                title="Hatch color"
                onChange={(event) => onHatchToolColorChange?.(event.target.value)}
              />
            </div>
          ) : iconToolVisible ? (
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-[#4b3e27]">Icon</span>
              <span className="text-[11px] font-medium text-[#4b3e27]">Color</span>
              <input
                className="h-8 w-10 cursor-pointer rounded-md border border-[rgba(74,58,31,0.16)] bg-white p-1"
                type="color"
                value={iconToolColor}
                aria-label="Icon main color"
                title="Icon main color"
                onChange={(event) => onIconToolColorChange?.(event.target.value)}
              />
              <span className="text-[11px] font-medium text-[#4b3e27]">Scale</span>
              <input
                className="h-8 w-16 rounded-md border border-[rgba(74,58,31,0.16)] bg-white px-2.5 text-[11px] font-medium text-[#111827] outline-none focus:border-[#1d5cab]"
                value={iconToolScale}
                inputMode="decimal"
                onChange={(event) => onIconToolScaleChange?.(event.target.value)}
              />
            </div>
          ) : textToolVisible ? (
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-[#4b3e27]">Text</span>
              <input
                className="h-8 w-28 rounded-md border border-[rgba(74,58,31,0.16)] bg-white px-2.5 text-[11px] font-medium text-[#111827] outline-none focus:border-[#1d5cab]"
                value={textToolValue}
                onChange={(event) => onTextToolValueChange?.(event.target.value)}
              />
              <span className="text-[11px] font-medium text-[#4b3e27]">Height</span>
              <input
                className="h-8 w-16 rounded-md border border-[rgba(74,58,31,0.16)] bg-white px-2.5 text-[11px] font-medium text-[#111827] outline-none focus:border-[#1d5cab]"
                value={textToolHeight}
                inputMode="decimal"
                onChange={(event) => onTextToolHeightChange?.(event.target.value)}
              />
              <select
                className="h-8 w-24 rounded-md border border-[rgba(74,58,31,0.16)] bg-white px-2 text-[11px] font-medium text-[#111827] outline-none focus:border-[#1d5cab]"
                value={textToolJustification}
                onChange={(event) => onTextToolJustificationChange?.(event.target.value)}
              >
                <option value="left">Left</option>
                <option value="middle">Middle</option>
                <option value="right">Right</option>
                <option value="bottom">Bottom</option>
              </select>
              <div className="relative">
                <button
                  type="button"
                  className="inline-flex h-8 w-10 items-center justify-center rounded-md border border-[rgba(74,58,31,0.16)] bg-white p-1 transition active:scale-110"
                  aria-label="Text color"
                  title="Text color"
                  onClick={() => setIsTextColorPaletteOpen((current) => !current)}
                >
                  <span
                    className="h-full w-full rounded-[4px] border border-[rgba(74,58,31,0.18)]"
                    style={{ backgroundColor: textToolColor }}
                    aria-hidden="true"
                  />
                </button>

                {isTextColorPaletteOpen && (
                  <div className="absolute left-1/2 top-full z-40 mt-2 grid w-40 -translate-x-1/2 grid-cols-5 gap-1.5 rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(245,240,230,0.98)] p-2 shadow-[0_14px_28px_rgba(58,45,20,0.14)]">
                    {AUTOCAD_STANDARD_COLORS.map((color) => {
                      const isActive = textToolColor.toLowerCase() === color.value;

                      return (
                        <button
                          key={color.aci}
                          type="button"
                          className={`aspect-square w-full rounded-md border transition active:scale-110 ${
                            isActive
                              ? 'border-[#1d5cab] ring-2 ring-[#1d5cab]/45'
                              : 'border-[rgba(74,58,31,0.18)]'
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={`ACI ${color.aci} - ${color.label}`}
                          aria-label={`ACI ${color.aci} - ${color.label}`}
                          onClick={() => {
                            onTextToolColorChange?.(color.value);
                            setIsTextColorPaletteOpen(false);
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
              <select
                className="h-8 w-32 rounded-md border border-[rgba(74,58,31,0.16)] bg-white px-2 text-[11px] font-medium text-[#111827] outline-none focus:border-[#1d5cab]"
                value={textToolFont}
                onChange={(event) => onTextToolFontChange?.(event.target.value)}
              >
                {textToolFonts.map((font) => (
                  <option key={font} value={font}>
                    {font}
                  </option>
                ))}
              </select>
            </div>
          ) : numericEditVisible ? (
            <div className="flex items-center gap-2">
              {showDirectionInput && (
                <>
                  <span className="text-[11px] font-medium text-[#4b3e27]">Direzione</span>
                  <input
                    ref={directionInputRef}
                    className="h-8 w-20 rounded-md border border-[rgba(74,58,31,0.16)] bg-white px-2.5 text-[11px] font-medium text-[#111827] outline-none focus:border-[#1d5cab]"
                    value={directionValue}
                    onFocus={onDirectionFocus}
                    onChange={(event) => onDirectionChange(event.target.value)}
                    onKeyDown={onDirectionKeyDown}
                  />
                  <span className="text-[11px] font-medium text-[#6b7280]">°</span>
                </>
              )}

              {showLineDistanceInput && (
                <>
                  <span className="text-[11px] font-medium text-[#4b3e27]">Linea</span>
                  <input
                    ref={lineDistanceInputRef}
                    className="h-8 w-24 rounded-md border border-[rgba(74,58,31,0.16)] bg-white px-2.5 text-[11px] font-medium text-[#111827] outline-none focus:border-[#1d5cab]"
                    value={lineDistanceValue}
                    onFocus={onLineDistanceFocus}
                    onChange={(event) => onLineDistanceChange(event.target.value)}
                    onKeyDown={onLineDistanceKeyDown}
                  />
                  <span className="text-[11px] font-medium text-[#6b7280]">{unitLabel}</span>
                </>
              )}

              <span className="text-[11px] font-medium text-[#4b3e27]">{numericLabel}</span>
              <input
                ref={numericInputRef}
                className="h-8 w-24 rounded-md border border-[rgba(74,58,31,0.16)] bg-white px-2.5 text-[11px] font-medium text-[#111827] outline-none focus:border-[#1d5cab]"
                value={numericValue}
                onFocus={onNumericFocus}
                onChange={(event) => onNumericChange(event.target.value)}
                onKeyDown={onNumericKeyDown}
              />
              <span className="text-[11px] font-medium text-[#6b7280]">{unitLabel}</span>
              <button
                type="button"
                className={lightActionButtonClass}
                onClick={onCommitNumericEdit}
              >
                Apply
              </button>
            </div>
          ) : (
            <div className="flex min-w-[200px] items-center justify-center rounded-md border border-dashed border-[rgba(74,58,31,0.16)] bg-[rgba(255,255,255,0.68)] px-3 py-2 text-[10px] text-[#6c5b3d]">
              No active input
            </div>
          )}
        </div>

        {activeSystemTitle && (
          <div className="flex min-w-[260px] max-w-[340px] flex-col gap-0.5 rounded-md border border-[rgba(29,92,171,0.14)] bg-[rgba(255,255,255,0.72)] px-3 py-1.5">
            <div className="truncate text-[9px] font-bold uppercase tracking-[0.08em] text-[#1d5cab]">
              {activeSystemTitle}
            </div>
            <div className="truncate text-[11px] font-semibold text-[#111827]">
              {activeSystemCodes || 'No code'}
            </div>
            <div className="truncate text-[10px] text-[#4b5563]">
              {activeSystemProducts || 'No additional products'}
            </div>
          </div>
        )}

        <div className="flex flex-col items-center gap-1.5">
          <div className="rounded-full bg-[rgba(29,92,171,0.08)] px-2 py-0.5 text-[8px] font-semibold uppercase tracking-[0.08em] text-[#1d5cab]">
            {historyStatusLabel}
          </div>

          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              disabled={!canUndo}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md border transition active:scale-110 disabled:cursor-not-allowed disabled:opacity-40 ${
                canUndo
                  ? 'border-[rgba(74,58,31,0.12)] bg-[rgba(255,255,255,0.9)] text-[#1F2937]'
                  : 'border-[rgba(74,58,31,0.08)] bg-[rgba(255,255,255,0.55)] text-[#9ca3af]'
              }`}
              aria-label="Undo"
              title="Undo (Ctrl+Z)"
              onClick={onUndo}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                <path d="M9 7 4 12l5 5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M20 12H4" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>

            <button
              type="button"
              disabled={!canRedo}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md border transition active:scale-110 disabled:cursor-not-allowed disabled:opacity-40 ${
                canRedo
                  ? 'border-[rgba(74,58,31,0.12)] bg-[rgba(255,255,255,0.9)] text-[#1F2937]'
                  : 'border-[rgba(74,58,31,0.08)] bg-[rgba(255,255,255,0.55)] text-[#9ca3af]'
              }`}
              aria-label="Redo"
              title="Redo (Ctrl+Y / Ctrl+Shift+Z)"
              onClick={onRedo}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                <path d="m15 7 5 5-5 5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M4 12h16" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
